import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ROOMS, roomForPath } from "@/lib/rooms";
import { Glyph } from "@/components/rooms/Glyph";
import { useActiveRoom } from "@/components/rooms/useActiveRoom";
import { rememberRoom, resumePending } from "@/lib/thread";

const HIDDEN = [/^\/lumen(\/|$)/, /^\/lab\/(inbox|publish)(\/|$)/, /^\/about\/testing\/admin/, /^\/payment\//, /^\/about\/testing\/checkout/, /^\/topography(\/|$)/, /^\/prism(\/|$)/];

// A visitor who lands mid-walk (deep link, subpage) is told the room once: numeral and word, held a little longer.
let announcedArrival = false;

const Node = ({ room, active, mobile, onPick, announce }) => (
  <Link
    to={room.to}
    onClick={onPick}
    data-testid={`rail-${room.id}${mobile ? "-mobile" : ""}`}
    data-cursor
    aria-label={`${room.numeral} · ${room.word}`}
    aria-current={active ? "location" : undefined}
    className={`group relative flex items-center justify-center transition-colors duration-500 ${mobile ? "h-11 w-11" : "h-9 w-9"} ${
      active ? "text-[#84DFF4]" : "text-[#F5F5F7]/35 hover:text-[#F5F5F7]/85"
    }`}
  >
    <Glyph kind={room.glyph} active={active} />
    {!mobile && (
      <span
        className={`pointer-events-none absolute right-full mr-3 whitespace-nowrap font-mono-spec text-[0.56rem] tracking-[0.3em] transition-opacity duration-500 ${
          active ? "text-[#8E8E93]" : "text-[#F5F5F7]/70"
        } opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 ${active ? "rail-word-active" : ""}`}
        data-testid={active ? "rail-active-word" : undefined}
      >
        {active && announce ? <span className="text-[#84DFF4]">{room.numeral}</span> : null}
        {active && announce ? <span aria-hidden="true" className="mx-2 inline-block h-px w-4 translate-y-[-2px] bg-[#8E8E93]/50" /> : null}
        {room.word}
      </span>
    )}
  </Link>
);

export default function Rail() {
  const { pathname, hash } = useLocation();
  const fallback = roomForPath(pathname);
  const active = useActiveRoom(fallback);
  const [settled, setSettled] = useState(false);
  const [arrival, setArrival] = useState(() => !announcedArrival && (pathname !== "/" || Boolean(hash) || Boolean(resumePending(pathname, hash))));

  // The thread: remember where the visitor stands on the home, so the next opening resumes there.
  useEffect(() => { if (pathname === "/") rememberRoom(active); }, [pathname, active]);

  useEffect(() => {
    if (!arrival) return undefined;
    announcedArrival = true;
    const t = setTimeout(() => setArrival(false), 4200);
    return () => clearTimeout(t);
  }, [arrival]);

  // The active word shows itself briefly when the viewer enters a new room, then recedes.
  useEffect(() => {
    setSettled(false);
    const t = setTimeout(() => setSettled(true), arrival ? 3600 : 1800);
    return () => clearTimeout(t);
  }, [active, arrival]);

  if (HIDDEN.some((re) => re.test(pathname))) return null;

  const pick = (room) => (e) => {
    if (pathname !== "/" || !room.to.startsWith("/#")) return;
    const el = document.getElementById(room.to.slice(2));
    if (!el) return;
    e.preventDefault();
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(el, { offset: -8, duration: 1.8 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        data-testid="rail"
        aria-label="Rooms"
        className={`fixed right-4 top-1/2 z-40 hidden -translate-y-1/2 flex-col items-center gap-1 [filter:drop-shadow(0_0_6px_rgba(3,3,5,0.95))] lg:flex ${settled ? "" : "rail-reveal"} ${arrival && !settled ? "rail-arrival" : ""}`}
        data-arrival={arrival && !settled ? "true" : undefined}
      >
        {ROOMS.map((room) => (
          <Node key={room.id} room={room} active={room.id === active} onPick={pick(room)} announce={arrival} />
        ))}
      </nav>
      <nav
        data-testid="rail-mobile"
        aria-label="Rooms"
        className="fixed inset-x-0 bottom-0 z-40 flex justify-center pb-[max(0.35rem,env(safe-area-inset-bottom))] pt-6 lg:hidden"
        style={{ background: "linear-gradient(180deg, rgba(3, 3, 5,0) 0%, rgba(3, 3, 5,0.78) 45%, rgba(3, 3, 5,0.92) 100%)" }}
      >
        <div className="relative flex items-center gap-1">
          <span
            data-testid="rail-mobile-word"
            className="pointer-events-none absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono-spec text-[0.52rem] tracking-[0.3em] text-[#8E8E93] transition-opacity duration-700"
            style={{ opacity: settled ? 0 : 1 }}
          >
            {arrival ? <span className="text-[#84DFF4]">{ROOMS.find((r) => r.id === active)?.numeral} · </span> : null}
            {ROOMS.find((r) => r.id === active)?.word}
          </span>
          {ROOMS.map((room) => (
            <Node key={room.id} room={room} active={room.id === active} mobile onPick={pick(room)} announce={arrival} />
          ))}
        </div>
      </nav>
    </>
  );
}
